/**
 * Admin: gestion de usuarios (Sprint 6 F).
 *
 * Lista usuarios del tenant con su estado (activo, MFA, ultimo login)
 * y permite:
 *   - Crear nuevo auditor (devuelve password temporal una sola vez)
 *   - Suspender / re-activar
 *   - Resetear MFA (forza setup en proximo login)
 *   - Cambiar role
 *
 * El password temporal se muestra UNA VEZ y debe compartirse out-of-band
 * por el admin con el usuario. Sprint 7 / hardening lo enviara por
 * email con magic-link de setup.
 */
import { type FormEvent, useEffect, useState } from 'react';

import { ApiError } from '../api/client.js';
import { type AdminUserCreated, type AdminUserItem, adminUsersApi } from '../api/admin-users.js';

export function AdminUsersPage(): JSX.Element {
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState<AdminUserCreated | null>(null);

  async function refresh(): Promise<void> {
    try {
      const r = await adminUsersApi.list();
      setUsers(r.users);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleToggleActive(u: AdminUserItem): Promise<void> {
    try {
      await adminUsersApi.update(u.id, { is_active: !u.is_active });
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.code);
      else setError(err instanceof Error ? err.message : 'Error');
    }
  }

  async function handleResetMfa(u: AdminUserItem): Promise<void> {
    if (
      !confirm(
        `Resetear MFA de ${u.email}? En su proximo login se le pedira configurar nuevamente.`,
      )
    )
      return;
    try {
      await adminUsersApi.resetMfa(u.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  }

  async function handleRoleChange(u: AdminUserItem, role: 'auditor' | 'admin'): Promise<void> {
    if (role === u.role) return;
    try {
      await adminUsersApi.update(u.id, { role });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400">Admin</p>
          <h1 className="text-xl font-semibold text-brand-900">Usuarios</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700/90"
        >
          Nuevo usuario
        </button>
      </header>

      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}

      {justCreated && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-900">Usuario creado</p>
          <p className="text-xs text-amber-800">
            Comparte estas credenciales con {justCreated.email} de forma segura. La password no se
            mostrara nuevamente.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="bg-white border border-amber-300 rounded p-2">
              <p className="text-[10px] uppercase tracking-widest text-amber-700">Email</p>
              <p className="font-mono text-xs text-amber-900">{justCreated.email}</p>
            </div>
            <div className="bg-white border border-amber-300 rounded p-2">
              <p className="text-[10px] uppercase tracking-widest text-amber-700">
                Password temporal
              </p>
              <p className="font-mono text-xs text-amber-900 break-all">
                {justCreated.temp_password}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setJustCreated(null)}
            className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Cerrar
          </button>
        </div>
      )}

      {showCreate && (
        <CreateUserForm
          onCancel={() => setShowCreate(false)}
          onCreated={(u) => {
            setJustCreated(u);
            setShowCreate(false);
            void refresh();
          }}
        />
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : (
        <table className="w-full text-sm bg-white border border-slate-200 rounded-xl overflow-hidden">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Rol</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">MFA</th>
              <th className="text-left px-3 py-2">Ultimo login</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 text-slate-800">{u.email}</td>
                <td className="px-3 py-2">
                  {u.role === 'super_admin' ? (
                    // Los super_admin solo se gestionan desde /platform/users.
                    // El dropdown de tenant no tiene esa opción y el backend
                    // rechazaría cualquier mutación (403 forbidden_super_admin).
                    <span
                      data-testid="badge-super-admin"
                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-cumple-primary-100 text-cumple-primary-800"
                    >
                      Super Admin
                    </span>
                  ) : (
                    <select
                      value={u.role}
                      onChange={(e) => {
                        void handleRoleChange(u, e.target.value as 'auditor' | 'admin');
                      }}
                      className="rounded border border-slate-300 px-2 py-1 text-xs"
                    >
                      <option value="auditor">auditor</option>
                      <option value="admin">admin</option>
                    </select>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={
                      'rounded-full px-2 py-0.5 text-[10px] font-medium ' +
                      (u.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600')
                    }
                  >
                    {u.is_active ? 'activo' : 'suspendido'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {u.mfa_configured ? 'configurado' : 'pendiente'}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('es-DO') : '—'}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  {u.role === 'super_admin' ? (
                    // Acciones de tenant deshabilitadas para super_admin.
                    <span
                      data-testid="super-admin-managed-label"
                      className="text-xs text-slate-400 italic"
                    >
                      Gestionado en plataforma
                    </span>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          void handleToggleActive(u);
                        }}
                        className="text-xs font-medium text-slate-600 hover:underline"
                      >
                        {u.is_active ? 'Suspender' : 'Reactivar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void handleResetMfa(u);
                        }}
                        className="text-xs font-medium text-red-700 hover:underline"
                      >
                        Reset MFA
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}

function CreateUserForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (u: AdminUserCreated) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'auditor' | 'admin'>('auditor');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await adminUsersApi.create({ email: email.trim(), role });
      onCreated(created);
    } catch (err) {
      if (err instanceof ApiError) setError(err.code);
      else setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      className="rounded-xl border border-slate-200 bg-white p-4 space-y-3"
    >
      <h2 className="font-semibold text-slate-900">Nuevo usuario</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label htmlFor="new-email" className="block text-xs font-medium text-slate-700">
            Email
          </label>
          <input
            id="new-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="new-role" className="block text-xs font-medium text-slate-700">
            Rol
          </label>
          <select
            id="new-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'auditor' | 'admin')}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="auditor">auditor</option>
            <option value="admin">admin</option>
          </select>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
        >
          {submitting ? 'Creando...' : 'Crear'}
        </button>
      </div>
    </form>
  );
}
